import { IdGenerator, IdGeneratorEntityType } from './IdGenerator';
import { parseTextIntoTokens } from './textParsing';

import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
  TranscriptPunctToken,
  TranscriptTokenSpeakerProps,
} from '../types';

/**
 * PLAT-10266 — the stable keys a set of replacement words should inherit from
 * the words they replace, positionally: the i-th entry is meant for the i-th
 * new TEXT token.
 *
 * A token's stable key is its `source_uuid` when it has one, otherwise its own
 * `id` (matching how the review and aligned-block readers resolve identity).
 *
 * PLAT-10266 Card I5 (2026-08-05) — EXCEPT when the replaced token is ITSELF an
 * insert (`initial_index < 0`), i.e. a word an earlier replacement spliced in.
 * Such a token has no story identity: its `id` is a freshly minted EDITOR id
 * from `IdGenerator`, and `source_uuid` is the translation JOIN key, never an
 * editor id (the epic's standing rule, PLAT-10611). Falling back to `id` there
 * wrote a value no phrase has ever covered into the join-key field, and both
 * review readers then read it as the NEXT row's territory and stopped: replacing
 * `secret` with `hidden truth` and then correcting `truth` to `truths` rendered
 * "little hidden" and re-translated as ["hidden"] — the correction disappeared
 * from the screen and from the backend payload.
 *
 * So an insert donates only a REAL `source_uuid`, which is how the true original
 * key travels down a chain of re-edits. A real transcript element still donates
 * `source_uuid ?? id`: a legacy element's identity IS its `id`, and that must
 * keep transferring.
 *
 * A key is emitted at most once. Live transcripts already carry the same
 * `source_uuid` on two unrelated words, and handing one key to two tokens would
 * make the translation join ambiguous — so a repeat is dropped and that
 * replacement word inherits nothing.
 */
export function inheritedSourceUuidsFor(
  replacedTextTokens?: readonly ClipTranscriptTextToken[],
): (string | undefined)[] {
  if (!replacedTextTokens) return [];

  const alreadyUsed = new Set<string>();
  return replacedTextTokens.map(token => {
    const key =
      token.initial_index < 0
        ? token.source_uuid
        : (token.source_uuid ?? token.id);
    if (!key || alreadyUsed.has(key)) return undefined;
    alreadyUsed.add(key);
    return key;
  });
}

/**
 * Splits a text value into multiple tokens with evenly distributed timestamps.
 * This is the core utility used by both transcript editing and segment creation.
 *
 * @param value - The text value to split (e.g., "hello world")
 * @param mediaStart - Media start time for the interval
 * @param relativeStart - Relative start time for the interval
 * @param relativeDuration - Available duration to distribute across words
 * @param initialIndex - Initial index for the tokens (defaults to -1)
 * @param state - State for the tokens (e.g., 'added', undefined)
 * @param context - Optional context properties to preserve (paragraph_id, speaker_id, etc.)
 * @param replacedTextTokens - Optional text tokens being replaced. The i-th new
 *   text token inherits the i-th replaced token's stable key into its
 *   `source_uuid` (PLAT-10266); see `inheritedSourceUuidsFor` above.
 * @returns Array of ClipTranscriptToken with timestamps evenly distributed
 */
export function splitTokenValueIntoTokens(
  value: string,
  mediaStart: number,
  relativeStart: number,
  relativeDuration: number,
  initialIndex: number = -1,
  state: ClipTranscriptToken['state'],
  context?: TranscriptTokenSpeakerProps,
  replacedTextTokens?: readonly ClipTranscriptTextToken[],
): ClipTranscriptToken[] {
  const parsedTokens = parseTextIntoTokens(value);
  const textTokensCount = parsedTokens.filter(t => t.type === 'text').length;

  const inheritedSourceUuids = inheritedSourceUuidsFor(replacedTextTokens);

  const generator = IdGenerator.get(
    IdGeneratorEntityType.TranscriptToken,
    'hex',
  );

  let perWordDuration = 0;
  if (textTokensCount > 0) {
    perWordDuration = Math.max(0, relativeDuration / textTokensCount);
  }

  let wordIndex = 0;
  return parsedTokens.map<ClipTranscriptToken>(raw => {
    if (raw.type === 'text') {
      const relativeStartTime = relativeStart + wordIndex * perWordDuration;
      const relativeEndTime =
        textTokensCount > 0
          ? relativeStart + (wordIndex + 1) * perWordDuration
          : relativeStart; // zero-length interval
      const mediaStartTime = mediaStart + (relativeStartTime - relativeStart);
      const mediaEndTime = mediaStart + (relativeEndTime - relativeStart);

      const inheritedSourceUuid = inheritedSourceUuids[wordIndex];

      const token: ClipTranscriptTextToken = {
        id: generator.getNextIdHex(),
        ...(inheritedSourceUuid !== undefined && {
          source_uuid: inheritedSourceUuid,
        }),
        type: 'text',
        value: raw.value,
        relative_ts: relativeStartTime,
        relative_end_ts: relativeEndTime,
        media_ts: mediaStartTime,
        media_end_ts: mediaEndTime,
        initial_index: initialIndex,
        state,
        // Preserve context properties
        ...(context?.paragraph_id && { paragraph_id: context.paragraph_id }),
        ...(context?.speaker_id && { speaker_id: context.speaker_id }),
        ...(context?.speaker_label && { speaker_label: context.speaker_label }),
        ...(context?.segment_id && { segment_id: context.segment_id }),
      };
      wordIndex += 1;
      return token;
    }
    const punctToken: TranscriptPunctToken = {
      id: generator.getNextIdHex(),
      type: 'punct',
      value: raw.value,
      initial_index: initialIndex,
      state,
    };
    return punctToken;
  });
}

/**
 * Checks if a text value contains multiple words (fast check using whitespace)
 * @param value - The text value to check
 * @returns True if the value contains whitespace (multiple words)
 */
export function isMultiWord(value: string): boolean {
  return /\s/.test(value);
}

/**
 * Splits ClipTranscriptToken elements that contain multiple words into separate word tokens.
 * Text tokens with multiple words will be split with evenly distributed timestamps.
 *
 * @param tokens - Array of ClipTranscriptToken to process
 * @returns New array with multi-word text tokens split into individual words
 */
export function splitMultiWordClipTokens(
  tokens: ClipTranscriptToken[],
): ClipTranscriptToken[] {
  const result: ClipTranscriptToken[] = [];

  for (const token of tokens) {
    // Pass through punctuation unchanged
    if (token.type !== 'text') {
      result.push(token);
      continue;
    }

    const textToken = token as ClipTranscriptTextToken;

    if (!textToken.value) {
      continue;
    }

    // Fast check: if no whitespace, it's a single word
    if (!isMultiWord(textToken.value)) {
      result.push(token);
      continue;
    }

    // Split the multi-word token into individual words
    const relativeDuration = textToken.relative_end_ts - textToken.relative_ts;
    const splitTokens = splitTokenValueIntoTokens(
      textToken.value,
      textToken.media_ts,
      textToken.relative_ts,
      relativeDuration,
      textToken.initial_index,
      textToken.state,
    );

    result.push(...splitTokens);
  }

  return result;
}
